<template>
	<div class="sidebar-container">
		<!-- Temple branding + switcher -->
		<div v-if="temple" class="sidebar-temple-section">
			<v-menu v-if="authStore.hasMultipleTemples" offset-y>
				<template v-slot:activator="{ props }">
					<div v-bind="props" class="temple-brand">
						<v-avatar size="40" rounded="lg" class="temple-avatar">
							<v-img v-if="temple.logoUrl" :src="temple.logoUrl" :alt="temple.name" />
							<v-icon v-else size="22" color="white">mdi-church</v-icon>
						</v-avatar>
						<div class="temple-info">
							<span :class="['temple-name-wrap', templeNameSizeClass]">{{ temple.name }}</span>
						</div>
						<v-icon size="16" class="temple-chevron">mdi-chevron-down</v-icon>
					</div>
				</template>

				<v-list density="compact" class="temple-switcher-list">
					<v-list-subheader>{{ $t("temple.yourTemples") }}</v-list-subheader>
					<v-list-item
						v-for="t in authStore.temples"
						:key="t.id"
						:active="t.id === authStore.user?.tenantId"
						@click="handleTempleSwitch(t.id)"
					>
						<template v-slot:prepend>
							<v-icon
								:icon="t.id === authStore.user?.tenantId ? 'mdi-check-circle' : 'mdi-church'"
								:color="t.primaryColor || undefined"
								size="20"
							/>
						</template>
						<v-list-item-title>{{ t.name }}</v-list-item-title>
						<v-list-item-subtitle>{{ getRoleLabel(t.role) }}</v-list-item-subtitle>
					</v-list-item>

					</v-list>
			</v-menu>

			<div v-else class="temple-brand">
				<v-avatar size="40" rounded="lg" class="temple-avatar">
					<v-img v-if="temple.logoUrl" :src="temple.logoUrl" :alt="temple.name" />
					<v-icon v-else size="22" color="white">mdi-church</v-icon>
				</v-avatar>
				<div class="temple-info">
					<span :class="['temple-name-wrap', templeNameSizeClass]">{{ temple.name }}</span>
				</div>
			</div>
		</div>

		<!-- Menu Dinâmico (scrollável) -->
		<div class="sidebar-menu">
			<v-list v-if="loading" class="pa-4">
				<v-progress-circular indeterminate color="primary"></v-progress-circular>
			</v-list>

			<v-list
				density="compact"
				nav
				v-else
				class="sidebar-nav"
				:opened="openedGroups"
				@update:opened="onOpenedUpdate"
				open-strategy="single"
			>
				<template v-for="item in menuItems" :key="item.id">
					<!-- Item com submenus -->
					<v-list-group
						v-if="item.children && item.children.length > 0"
						:value="item.id"
						class="menu-group"
					>
						<template v-slot:activator="{ props }">
							<v-list-item
								v-bind="props"
								:prepend-icon="item.icon || 'mdi-folder'"
								:title="item.title"
								class="group-title"
							/>
						</template>
						<v-list-item
							v-for="child in item.children"
							:key="child.id"
							:data-menu-id="child.id"
							:to="child.path || ''"
							:prepend-icon="child.icon || ''"
							:title="child.title"
							class="ms-4 menu-item"
							link
							@click="handleMenuClick"
						/>
					</v-list-group>

					<!-- Item sem submenus -->
					<v-list-item
						v-else
						:data-menu-id="item.id"
						:to="item.path || ''"
						:prepend-icon="item.icon || 'mdi-circle-small'"
						:title="item.title"
						class="menu-item"
						link
						@click="handleMenuClick"
					/>
				</template>
			</v-list>
		</div>

		<!-- Footer do Sidebar -->
		<div class="sidebar-footer">
			<v-list density="compact" nav class="sidebar-footer-nav">
				<v-list-item
					prepend-icon="mdi-book-open-page-variant"
					:title="$t('navigation.manual')"
					:href="manualUrl"
					target="_blank"
					class="menu-item"
				/>
				<v-list-item
					data-menu-id="support"
					prepend-icon="mdi-lifebuoy"
					:title="$t('navigation.support')"
					to="/support"
					class="menu-item"
					link
					@click="handleMenuClick"
				/>
			</v-list>

		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useAuthStore } from "@/stores/auth";
import { useRoute, useRouter } from "vue-router";
import { useDisplay } from "vuetify";
import menuService, { type MenuItem } from "@/services/menuService";


// Emits
const emit = defineEmits<{
	close: [];
}>();

// Composables
const { t, locale } = useI18n();
const authStore = useAuthStore();
const route = useRoute();
const router = useRouter();
const { mobile } = useDisplay();

// Computed
const user = computed(() => authStore.user);
const temple = computed(() => authStore.currentTemple);
const isHml = import.meta.env.VITE_APP_ENV === "hml";
const manualUrl = computed(() => isHml ? "https://manual-hml.oriapp.com.br" : "https://manual.oriapp.com.br");

const templeNameSizeClass = computed(() => {
	const len = temple.value?.name?.length || 0;
	if (len <= 18) return 'temple-name--md';
	if (len <= 28) return 'temple-name--sm';
	return 'temple-name--xs';
});


// Accordion via Vuetify open-strategy="single"
const openedGroups = ref<string[]>([]);

const activeGroupId = computed(() => {
	const currentPath = route.path;
	for (const item of menuItems.value) {
		if (item.children?.some((child) => child.path === currentPath)) {
			return item.id;
		}
	}
	return null;
});

const isActiveGroup = (item: MenuItem): boolean => {
	return activeGroupId.value === item.id;
};

const onOpenedUpdate = (val: string[]) => {
	// Vuetify single strategy already ensures max 1 open
	// But always keep the active group open
	if (activeGroupId.value && !val.includes(activeGroupId.value)) {
		openedGroups.value = [...val, activeGroupId.value];
	} else {
		openedGroups.value = val;
	}
};

// Refs
const menuItems = ref<MenuItem[]>([]);
const loading = ref(true);

// Methods
const filterMenuByRole = (items: MenuItem[]): MenuItem[] => {
	const isAdmin = authStore.isAdmin;
	return items
		.filter((item) => !item.adminOnly || isAdmin)
		.map((item) => {
			if (item.children) {
				return {
					...item,
					children: item.children.filter(
						(child) => !child.adminOnly || isAdmin
					),
				};
			}
			return item;
		})
		.filter((item) => !item.children || item.children.length > 0);
};

const fetchMenuItems = async () => {
	try {
		loading.value = true;
		const allMenus = await menuService.getUserAccessibleMenus();
		menuItems.value = filterMenuByRole(allMenus);
		if (activeGroupId.value) openedGroups.value = [activeGroupId.value];
	} catch (error) {
		console.error("Erro ao carregar menus:", error);
		menuItems.value = filterMenuByRole(menuService.getDevMenus());
		if (activeGroupId.value) openedGroups.value = [activeGroupId.value];
	} finally {
		loading.value = false;
	}
};

const handleMenuClick = () => {
	if (mobile.value) {
		emit("close");
	}
};

const handleTempleSwitch = async (templeId: string) => {
	if (templeId !== authStore.user?.tenantId) {
		await authStore.switchTemple(templeId);
	}
};

const getRoleLabel = (role: string): string => {
	const roleMap: Record<string, string> = {
		OWNER: t("temple.roles.owner"),
		ADMIN: t("temple.roles.admin"),
		MEMBER: t("temple.roles.member"),
	};
	return roleMap[role] || role;
};

/**
 * Expande o grupo que contém o menuId informado (para o tour guiado).
 */
const expandGroupForMenuId = (menuId: string) => {
	for (const item of menuItems.value) {
		if (item.children?.some((c) => c.id === menuId)) {
			if (!openedGroups.value.includes(item.id)) {
				openedGroups.value = [item.id];
			}
			return;
		}
	}
};

defineExpose({ expandGroupForMenuId });

// Lifecycle
onMounted(fetchMenuItems);

// Watch para mudanças de idioma
watch(locale, () => {
	// Atualizar menu quando o idioma mudar
	fetchMenuItems();
});
</script>

<style lang="scss" scoped>
@use "@/styles/_variables.scss" as vars;

// ============================================
// CONTAINER
// ============================================
.sidebar-container {
	display: flex;
	flex-direction: column;
	height: 100%;
	background: rgb(var(--v-theme-surface));
}

// ============================================
// TEMPLE BRANDING
// ============================================
.sidebar-temple-section {
	flex-shrink: 0;
	padding: 0.875rem 0.75rem;
	border-bottom: 1px solid rgba(var(--v-theme-primary), 0.08);
}

.temple-brand {
	display: flex;
	align-items: center;
	gap: 0.625rem;
	padding: 0.5rem;
	border-radius: 10px;
	cursor: pointer;
	transition: background 0.2s ease;

	&:hover {
		background: rgba(var(--v-theme-primary), 0.06);
	}
}

.temple-avatar {
	flex-shrink: 0;
	background: linear-gradient(
		135deg,
		rgb(var(--v-theme-primary)),
		rgba(var(--v-theme-primary), 0.7)
	);
}

.temple-info {
	flex: 1;
	min-width: 0;
}

.temple-chevron {
	flex-shrink: 0;
	opacity: 0.4;
}

.temple-name-wrap {
	display: -webkit-box !important;
	-webkit-box-orient: vertical;
	-webkit-line-clamp: 2;
	overflow: hidden !important;
	text-overflow: ellipsis !important;
	white-space: normal !important;
	line-height: 1.3;
	font-weight: 600;
	font-family: vars.$font-secondary;
	color: rgb(var(--v-theme-on-surface));
}

.temple-name--md {
	font-size: 0.85rem;
}

.temple-name--sm {
	font-size: 0.78rem;
}

.temple-name--xs {
	font-size: 0.7rem;
}

.temple-switcher-list {
	min-width: 220px;
}

// ============================================
// MENU
// ============================================
.sidebar-menu {
	flex: 1;
	overflow-y: auto;
	padding-top: 0.25rem;
}

.sidebar-nav {
	padding: 0.25rem 0.5rem;
}

// Menu items — themed active state
.menu-item {
	border-radius: 8px !important;
	margin-bottom: 1px;
	transition: all 0.2s ease;

	&:hover {
		background: rgba(var(--v-theme-primary), 0.06);
	}
}

// Active menu item — uses theme color
:deep(.v-list-item--active) {
	background: rgba(var(--v-theme-primary), 0.1) !important;
	color: rgb(var(--v-theme-primary)) !important;

	.v-list-item__prepend .v-icon {
		color: rgb(var(--v-theme-primary)) !important;
	}

	.v-list-item-title {
		font-weight: 600;
	}

	// Accent bar on left
	&::before {
		content: "";
		position: absolute;
		left: 0;
		top: 6px;
		bottom: 6px;
		width: 3px;
		border-radius: 0 3px 3px 0;
		background: rgb(var(--v-theme-primary));
	}
}

.menu-group {
	margin-top: 0.75rem;
}

.group-title {
	opacity: 1;
}

:deep(.group-title .v-list-item__content) {
	font-size: 0.825rem !important;
	font-weight: 700;
	letter-spacing: 0.02em;
	text-transform: uppercase;
	color: rgba(var(--v-theme-on-surface), 0.75) !important;
}

:deep(.group-title .v-list-item__prepend .v-icon) {
	opacity: 0.7;
}

.v-list-group .v-list-item.ms-4 {
	padding-left: 10px !important;
}

.v-list-group .v-list-item.ms-4 .v-list-item__prepend {
	padding-inline-end: 12px;
}

// ============================================
// FOOTER
// ============================================
.sidebar-footer {
	flex-shrink: 0;
	border-top: 1px solid rgba(var(--v-theme-primary), 0.08);
}

.sidebar-footer-nav {
	padding: 0.25rem 0.5rem;
}

</style>
